import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Droplets, Container, Truck, BarChart3, ChevronRight, LogOut, Satellite } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useAuth } from '@/lib/auth';
import { apiClient } from '@/lib/api';
import { useCurrentProject } from '@/contexts/CurrentProjectContext';

// PR-STEP2-3 — the three-tile home. The single operator lands here, picks a
// project box, and everything they open next is scoped to it. A fourth tile
// opens the cross-project owner view (directional profit per project).

interface ProjectRow {
  id: string;
  code: string;
  name_en: string;
  primary_city: string | null;
  status: string;
  vehicle_count?: number;
  driver_count?: number;
}

interface TileDef {
  code: string;
  icon: typeof Droplets;
  accent: string;        // border + icon tint
  title: string;
  subtitle: string;
  status: string;
}

/**
 * A status is a fact about the project, so it is coloured like one: live work
 * green, work in progress amber, something new indigo. One grey pill for all
 * three made them decorative.
 */
const PILL: Record<string, string> = {
  live: 'bg-[#ECFDF5] text-[#047857] dark:bg-emerald-500/10 dark:text-emerald-400',
  building: 'bg-[#FFFBEB] text-[#B45309] dark:bg-amber-500/10 dark:text-amber-400',
  progress: 'bg-[#FFFBEB] text-[#B45309] dark:bg-amber-500/10 dark:text-amber-400',
  new: 'bg-[#EEF2FF] text-[#4338CA] dark:bg-indigo-500/10 dark:text-indigo-400',
};
const pillFor = (status: string) => {
  const s = status.toLowerCase();
  if (s.startsWith('live')) return PILL.live;
  if (s.startsWith('building')) return PILL.building;
  if (s.startsWith('in progress')) return PILL.progress;
  if (s.startsWith('new')) return PILL.new;
  return 'bg-muted text-muted-foreground';
};

const TILES: TileDef[] = [
  { code: 'PRJ-NEOM',    icon: Droplets,  accent: 'text-sky-500',     title: 'NEOM',    subtitle: 'Water delivery · per m³ · investor split', status: 'Live' },
  { code: 'PRJ-DUMPERS', icon: Container, accent: 'text-amber-500',   title: 'Dumpers', subtitle: 'Per ton · ARASCO tickets · receivables',   status: 'Building (Step 3)' },
  { code: 'PRJ-RIYADH',  icon: Truck,     accent: 'text-emerald-500', title: 'Flatbed', subtitle: 'Per job · cost-to-serve · Riyadh',          status: 'In progress' },
  { code: 'PRJ-RIYADH-WATER', icon: Droplets, accent: 'text-cyan-500',   title: 'Riyadh Water', subtitle: 'Water delivery · Riyadh',            status: 'New' },
];

export default function HomePage() {
  const { user, logout, loadCurrentUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setProject } = useCurrentProject();
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) loadCurrentUser();
  }, [user, loadCurrentUser]);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ items: ProjectRow[] }>('/api/v1/projects')
      .then((r) => { if (!cancelled) setProjects(r.data.items); })
      .catch(() => { /* leave empty — tiles render as not-set-up */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Drivers / investors / supervisors have their own self-view home.
  if (user && (user.role === 'driver' || user.role === 'investor' || user.role === 'construction_supervisor')) {
    return <Navigate to="/dashboard" replace />;
  }

  const byCode = new Map(projects.map((p) => [p.code, p]));

  function enterProject(row: ProjectRow) {
    setProject({ id: row.id, code: row.code, nameEn: row.name_en });
    navigate('/dashboard');
  }

  function openOwnerView() {
    setProject(null); // owner view reads across all projects
    navigate('/dashboard/owner');
  }

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10 backdrop-blur-sm bg-card/80">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="" width={36} height={36} className="rounded-lg shrink-0" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
              <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="ms-2 ps-2 border-s border-border">
              <Button variant="ghost" size="sm" onClick={logout} className="gap-2">
                <LogOut className="h-4 w-4" />
                <span className="hidden md:inline">{t('auth.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-12">
        <div className="text-center mb-10">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display text-3xl md:text-4xl text-foreground tracking-tight">
                {t('home.pick', { defaultValue: 'Select workspace' })}
              </h1>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">
                {t('home.subtitle', {
                  defaultValue: 'Manage operational fleets, job tickets and driver allocations per project.' })}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TILES.map((tile) => {
            const row = byCode.get(tile.code);
            const Icon = tile.icon;
            const ready = !!row;
            return (
              <button
                key={tile.code}
                type="button"
                disabled={!ready}
                onClick={() => row && enterProject(row)}
                className={`group text-start rounded-xl border border-border bg-card p-5
                  transition-all duration-200
                  ${ready
                    ? 'cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-black/5 hover:border-[#C59B27] hover:ring-1 hover:ring-[#C59B27]/30'
                    : 'opacity-60 cursor-not-allowed'}`}
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <span className={`h-10 w-10 rounded-lg bg-muted/60 flex items-center justify-center shrink-0 ${tile.accent}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-1 font-medium
                    ${ready ? pillFor(tile.status) : 'bg-muted text-muted-foreground'}`}>
                    {ready ? tile.status : t('home.notSetUp', { defaultValue: 'Not set up yet' })}
                  </span>
                </div>
                <div className="font-display text-lg text-foreground">{tile.title}</div>
                <div className="text-sm text-muted-foreground mt-1 leading-snug">{tile.subtitle}</div>
                {ready && (
                  <>
                    {/* The two numbers that say how big this project is, split so
                        neither is read as part of the other. */}
                    <div className="mt-4 pt-3 border-t border-border grid grid-cols-2 divide-x divide-border">
                      <div className="pe-3">
                        <div className="text-lg font-mono text-foreground">{row?.vehicle_count ?? 0}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {t('home.vehicles', { defaultValue: 'vehicles' })}
                        </div>
                      </div>
                      <div className="ps-3">
                        <div className="text-lg font-mono text-foreground">{row?.driver_count ?? 0}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {t('home.drivers', { defaultValue: 'drivers' })}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-end text-xs text-[#C59B27] opacity-0 group-hover:opacity-100 transition-opacity">
                      Open <ChevronRight className="h-4 w-4" />
                    </div>
                  </>
                )}
              </button>
            );
          })}
        </div>

        {/* Owner view — the only thing that crosses projects */}
        <div className="mt-6">
          <button
            type="button"
            onClick={openOwnerView}
            className="group w-full text-start rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap
              bg-gradient-to-br from-[#0F172A] to-[#1E293B] border-t-2 border-[#C59B27]
              transition-all duration-200 hover:shadow-lg hover:shadow-black/20"
          >
            <div className="flex items-center gap-4">
              <span className="h-11 w-11 rounded-full bg-white/5 ring-1 ring-[#C59B27]/30 flex items-center justify-center shrink-0">
                <BarChart3 className="h-5 w-5 text-[#C59B27]" />
              </span>
              <div>
                <div className="font-display text-lg text-white">
                  {t('home.owner', { defaultValue: 'Executive owner portal' })}
                </div>
                <div className="text-sm text-slate-400">
                  {t('home.ownerSub', {
                    defaultValue: 'Profitability, top-up tracking and project financials — directional for now' })}
                </div>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 rounded-lg border border-[#C59B27]/60 px-4 py-2
                             text-sm text-[#E8C77A] transition-colors group-hover:bg-[#C59B27]/10">
              {t('home.ownerCta', { defaultValue: 'Launch executive view' })}
              <ChevronRight className="h-4 w-4" />
            </span>
          </button>
        </div>

        {/* Fleet Data Explorer — every truck's daily km, meters, diesel and raw GPS, downloadable */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard/fleet/explorer')}
            className="group w-full text-start rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap
              bg-card border border-border border-t-2 border-t-amber-500
              transition-all duration-200 hover:shadow-lg hover:shadow-black/5 hover:border-amber-500/50"
          >
            <div className="flex items-center gap-4">
              <span className="h-11 w-11 rounded-full bg-amber-500/10 ring-1 ring-amber-500/30 flex items-center justify-center shrink-0">
                <Satellite className="h-5 w-5 text-amber-500" />
              </span>
              <div>
                <div className="font-display text-lg text-foreground">Fleet Data Explorer</div>
                <div className="text-sm text-muted-foreground">
                  Every truck, every day — distance, start/end meters, diesel, stops and raw GPS · fully downloadable
                </div>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 rounded-lg border border-amber-500/60 px-4 py-2
                             text-sm text-amber-600 dark:text-amber-400 transition-colors group-hover:bg-amber-500/10">
              Open explorer
              <ChevronRight className="h-4 w-4" />
            </span>
          </button>
        </div>

        {loading && (
          <p className="text-center text-xs text-muted-foreground mt-8">
            {t('common.loading', { defaultValue: 'Loading…' })}
          </p>
        )}
      </main>

      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        © Vida Group · Saudi Arabia
      </footer>
    </div>
  );
}
